/*
 * Purchase overlay controller + consolidated countdown ticker.
 * Externalized from snippets/variant-picker.liquid (7 inline blocks) and
 * snippets/countdown-timer.liquid.
 *
 * Design notes:
 * - The theme re-renders the variant-picker block via the Section Rendering API
 *   (DOMParser fragments), so inline scripts inside the block never re-execute.
 *   Everything here is document-level delegation; per-instance data lives in the
 *   DOM (script[data-vp-config] + script[data-variant-stock] JSON blocks) and is
 *   read at event time.
 * - One shared 1s interval drives every countdown on the page:
 *   the purchase-overlay footer countdown (id="overlay-countdown-*") and all
 *   <countdown-timer> elements (countdown-bar, bundle promo).
 */
(function () {
  'use strict';
  if (window.__sneakVariantPickerCustom) return;
  window.__sneakVariantPickerCustom = true;

  var doc = document;

  /* ==================================================================
   * SHARED COUNTDOWN TICKER (one interval for the whole page)
   * ================================================================== */

  var cdState = new WeakMap(); /* per-element parsed state; re-rendered elements get fresh state */

  function midnight() {
    var now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0);
  }
  function pad(n) { return String(n).padStart(2, '0'); }

  /* Overlay footer countdown (was inline in variant-picker.liquid).
   * Loop mode: daily midnight reset. Days shown only in fixed mode with d > 0. */
  function tickOverlayCountdown(el) {
    var st = cdState.get(el);
    if (!st) {
      st = { loop: el.dataset.mode === 'loop' };
      st.end = st.loop ? midnight() : new Date(el.dataset.end);
      cdState.set(el, st);
    }
    var diff = st.end - new Date();
    if (diff <= 0) {
      if (st.loop) {
        st.end = midnight();
        diff = st.end - new Date();
      } else {
        diff = 0;
      }
    }
    var d = Math.floor(diff / 864e5);
    var h = Math.floor((diff % 864e5) / 36e5);
    var m = Math.floor((diff % 36e5) / 6e4);
    var s = Math.floor((diff % 6e4) / 1e3);
    var dEl = el.querySelector('[data-days]');
    var dSep = el.querySelector('[data-days-sep]');
    if (dEl) {
      var show = !st.loop && d > 0;
      dEl.style.display = show ? '' : 'none';
      if (dSep) dSep.style.display = show ? '' : 'none';
      dEl.textContent = pad(d);
    }
    var hEl = el.querySelector('[data-hours]');
    var mEl = el.querySelector('[data-minutes]');
    var sEl = el.querySelector('[data-seconds]');
    if (hEl) hEl.textContent = pad(h);
    if (mEl) mEl.textContent = pad(m);
    if (sEl) sEl.textContent = pad(s);
  }

  /* <countdown-timer> elements (was the CountdownTimer custom element in
   * countdown-timer.liquid). Supports daily loop (data-loop="daily" or
   * data-end-date="loop"), fixed dates, and epoch data-end-timestamp.
   * Invalid dates fall back to the daily-midnight loop. Days always padded,
   * never hidden. Non-loop timers freeze at 00 once expired. */
  function tickCountdownElement(el) {
    var st = cdState.get(el);
    if (!st) {
      st = { done: false };
      st.loop = el.dataset.loop === 'daily' || el.dataset.endDate === 'loop';
      if (st.loop) {
        st.end = midnight();
      } else if (el.dataset.endDate) {
        st.end = new Date(el.dataset.endDate);
        if (isNaN(st.end.getTime())) {
          st.end = midnight();
          st.loop = true;
        }
      } else {
        st.end = new Date(parseInt(el.dataset.endTimestamp, 10) * 1000);
      }
      cdState.set(el, st);
    }
    if (st.done) return;

    var daysEl = el.querySelector('[data-days]');
    var hoursEl = el.querySelector('[data-hours]');
    var minutesEl = el.querySelector('[data-minutes]');
    var secondsEl = el.querySelector('[data-seconds]');

    var diff = st.end - new Date();
    if (diff <= 0) {
      if (st.loop) {
        st.end = midnight();
        diff = st.end - new Date();
      } else {
        if (daysEl) daysEl.textContent = '00';
        if (hoursEl) hoursEl.textContent = '00';
        if (minutesEl) minutesEl.textContent = '00';
        if (secondsEl) secondsEl.textContent = '00';
        st.done = true;
        return;
      }
    }
    var days = Math.floor(diff / 864e5);
    var hours = Math.floor((diff % 864e5) / 36e5);
    var minutes = Math.floor((diff % 36e5) / 6e4);
    var seconds = Math.floor((diff % 6e4) / 1e3);
    if (daysEl) daysEl.textContent = pad(days);
    if (hoursEl) hoursEl.textContent = pad(hours);
    if (minutesEl) minutesEl.textContent = pad(minutes);
    if (secondsEl) secondsEl.textContent = pad(seconds);
  }

  function tickAllCountdowns() {
    var overlayEls = doc.querySelectorAll('.purchase-overlay__countdown[id^="overlay-countdown-"]');
    for (var i = 0; i < overlayEls.length; i++) tickOverlayCountdown(overlayEls[i]);
    var timerEls = doc.querySelectorAll('countdown-timer');
    for (var j = 0; j < timerEls.length; j++) tickCountdownElement(timerEls[j]);
  }

  /* ==================================================================
   * CONFIG + STOCK DATA (read from DOM so re-rendered values win)
   * ================================================================== */

  var configCache = { el: null, data: null };
  function getConfig() {
    var el = doc.querySelector('script[data-vp-config]');
    if (!el) return null;
    if (configCache.el === el) return configCache.data;
    var data = null;
    try { data = JSON.parse(el.textContent); } catch (e) { console.warn('vp config parse error', e); }
    configCache = { el: el, data: data };
    return data;
  }
  function getI18n() {
    var cfg = getConfig();
    return (cfg && cfg.i18n) || {};
  }
  function getFormId() {
    var cfg = getConfig();
    return cfg ? cfg.formId : null;
  }

  var stockCache = { el: null, map: {} };
  function getStockMap() {
    var el = doc.querySelector('script[data-variant-stock]');
    if (!el) return {};
    if (stockCache.el === el) return stockCache.map;
    var map = {};
    try { map = JSON.parse('{' + el.textContent + '}'); } catch (e) { console.warn('Stock data parse error', e); }
    stockCache = { el: el, map: map };
    return map;
  }

  /* ==================================================================
   * ELEMENT ACCESSORS (resolved at event time — survive re-render)
   * ================================================================== */

  function $(sel) { return doc.querySelector(sel); }
  function getTrigger() { return $('[data-purchase-overlay-trigger]'); }
  function getDrawer() { return $('[data-purchase-overlay]'); }
  function getAtcBtn() { return $('[data-purchase-overlay-atc]'); }
  function getAtcLabel() { var b = getAtcBtn(); return b ? b.querySelector('[data-atc-label]') : null; }
  function getAtcPrice() { var b = getAtcBtn(); return b ? b.querySelector('[data-atc-price]') : null; }
  function getFooter() { return $('[data-overlay-footer]'); }
  function getPostAtc() { return $('[data-overlay-post-atc]'); }
  function getSizeContent() { return $('[data-overlay-content="size"]'); }
  function getBundleContent() { return $('[data-overlay-content="bundle"]'); }
  function getTabs() { return doc.querySelectorAll('[data-overlay-tab]'); }
  function getUpsellSlot() { return $('[data-purchase-overlay-upsell]'); }
  function getBundleSlot() { return $('[data-purchase-overlay-bundle]'); }
  function getBundleBanner() { return $('[data-bundle-banner]'); }
  function getGrid() {
    var drawer = getDrawer();
    return drawer ? drawer.querySelector('.variant-picker__option-values') : null;
  }
  function getStockStatusEl() { return $('[data-overlay-stock-status]'); }
  function getStockTextEl() { var el = getStockStatusEl(); return el ? el.querySelector('[data-stock-text]') : null; }
  function getStockIconEl() { var el = getStockStatusEl(); return el ? el.querySelector('[data-stock-icon]') : null; }

  /* ==================================================================
   * STATE (module-level: survives DOM re-render, single overlay per page —
   * same assumption as the original inline controller)
   * ================================================================== */

  var STATES = { CLOSED: 'CLOSED', SIZE_SELECT: 'SIZE_SELECT', BUNDLE: 'BUNDLE', POST_ATC: 'POST_ATC' };
  var state = STATES.CLOSED;
  var confirmedVariantId = null;
  var subConfirmed = false;
  var bundleReady = false;
  var hasBundle = false;
  var bundleStockTimer = null;
  var originalUpsellParent = null;
  var originalUpsellNext = null;
  var originalBundleParent = null;
  var originalBundleNext = null;

  /* ==================== RERENDER LOCK ==================== */

  function lockRerender() {
    var drawer = getDrawer();
    var vpBlock = drawer ? drawer.closest('[data-block-type]') : null;
    if (vpBlock) {
      vpBlock.setAttribute('data-block-type-locked', vpBlock.getAttribute('data-block-type'));
      /* Preserve computed padding before removing the attribute */
      var cs = window.getComputedStyle(vpBlock);
      vpBlock.style.paddingTop = cs.paddingTop;
      vpBlock.style.paddingBottom = cs.paddingBottom;
      vpBlock.removeAttribute('data-block-type');
    }
  }
  function unlockRerender() {
    var vpBlock = doc.querySelector('[data-block-type-locked]');
    if (vpBlock) {
      vpBlock.setAttribute('data-block-type', vpBlock.getAttribute('data-block-type-locked'));
      vpBlock.removeAttribute('data-block-type-locked');
      vpBlock.style.paddingTop = '';
      vpBlock.style.paddingBottom = '';
    }
  }

  /* ==================== STOCK STATUS ==================== */

  function updateStockStatus() {
    var stockStatusEl = getStockStatusEl();
    var stockTextEl = getStockTextEl();
    var stockIconEl = getStockIconEl();
    if (!stockStatusEl || !stockTextEl) return;
    var formId = getFormId();
    var form = formId ? doc.getElementById(formId) : null;
    if (!form) return;
    var variantInput = form.querySelector('input[name="id"]');
    if (!variantInput || !variantInput.value) { stockStatusEl.style.display = 'none'; return; }

    var vid = variantInput.value;
    var info = getStockMap()[vid];
    if (!info) { stockStatusEl.style.display = 'none'; return; }

    stockStatusEl.style.display = 'flex';

    var i18n = getI18n();
    var variantTitle = info.title || '';

    if (!info.available) {
      stockIconEl.style.display = 'none';
      stockTextEl.textContent = i18n.stockSoldOut;
      stockStatusEl.className = 'purchase-overlay__stock-status';
    } else if (info.qty > 0 && info.qty <= 5) {
      stockIconEl.style.display = '';
      stockIconEl.style.color = '';
      stockIconEl.setAttribute('viewBox', '0 0 512 512');
      stockIconEl.innerHTML = '<use href="#48h-icon"></use>';
      stockTextEl.textContent = i18n.stockLow.replace('__COUNT__', info.qty).replace('__VARIANT__', variantTitle);
      /* The last one or two of a size read bold: a real count, worth noticing */
      stockStatusEl.className = 'purchase-overlay__stock-status' + (info.qty <= 2 ? ' is-low' : '');
    } else if (info.qty > 5) {
      stockIconEl.style.display = '';
      stockIconEl.style.color = '';
      stockIconEl.setAttribute('viewBox', '0 0 512 512');
      stockIconEl.innerHTML = '<use href="#48h-icon"></use>';
      stockTextEl.textContent = i18n.stockFew.replace('__VARIANT__', variantTitle);
      stockStatusEl.className = 'purchase-overlay__stock-status';
    } else if (info.backorderDays > 0) {
      /* A supplier or retail site has this size: about a week (custom.backorder_days, Sneak app) */
      var weeks = Math.max(1, Math.round(info.backorderDays / 7));
      stockIconEl.style.display = '';
      stockIconEl.style.color = 'var(--sneak-green)';
      stockIconEl.setAttribute('viewBox', '0 0 24 24');
      stockIconEl.innerHTML = '<use href="#backorder-icon"></use>';
      stockTextEl.textContent = weeks === 1 ? i18n.stockBackorder1 : String(i18n.stockBackorderN || '').replace('__COUNT__', weeks);
      stockStatusEl.className = 'purchase-overlay__stock-status';
    } else if (info.incoming) {
      stockIconEl.style.display = '';
      stockIconEl.style.color = 'var(--sneak-green)';
      stockIconEl.setAttribute('viewBox', '0 0 24 24');
      stockIconEl.innerHTML = '<use href="#backorder-icon"></use>';
      /* Incoming is "ships in about a week" now, same as backorder_days. */
      stockTextEl.textContent = i18n.stockBackorder1;
      stockStatusEl.className = 'purchase-overlay__stock-status';
    } else {
      stockIconEl.style.display = 'none';
      stockTextEl.textContent = i18n.stockPreorder;
      stockStatusEl.className = 'purchase-overlay__stock-status';
    }
  }

  /* ==================== BUNDLE DETECTION ==================== */

  function detectBundle() {
    var bundleBanner = getBundleBanner();
    var bEl = doc.querySelector('.product-info__bundle');
    var found = !!(bEl && bEl.querySelector('.integrated-bundle-wrapper, .bundle-interface, [data-bundle-wrapper]'));
    if (found && bundleBanner) {
      var bundlePrice = bEl.querySelector('.bundle-current-price');
      var comparePrice = bEl.querySelector('.bundle-compare-price');

      var textEl = bundleBanner.querySelector('[data-bundle-banner-text]');
      var priceEl2 = bundleBanner.querySelector('[data-bundle-banner-price]');
      var compareEl = bundleBanner.querySelector('[data-bundle-banner-compare]');

      if (textEl) textEl.textContent = getI18n().bundleBanner;
      if (priceEl2 && bundlePrice) priceEl2.textContent = bundlePrice.textContent.trim();
      if (compareEl && comparePrice) compareEl.textContent = comparePrice.textContent.trim();

      /* Only show banner if there's actual price content */
      var hasPriceContent = bundlePrice && bundlePrice.textContent.trim();
      if (hasPriceContent) bundleBanner.style.display = '';
    }
    hasBundle = found;
    return found;
  }

  /* ==================== STATE MACHINE ==================== */

  function setState(newState) {
    state = newState;
    switch (state) {
      case STATES.CLOSED: closeOverlay(); break;
      case STATES.SIZE_SELECT: showSizeTab(); break;
      case STATES.BUNDLE: showBundleTab(); break;
      case STATES.POST_ATC: showPostAtc(); break;
    }
  }

  /* ==================== OVERLAY OPEN / CLOSE ==================== */

  function openOverlay() {
    var drawer = getDrawer();
    if (!drawer) return;
    lockRerender();
    relocateElements();
    state = STATES.SIZE_SELECT;
    showSizeTab();
    if (drawer.show) {
      drawer.show();
    } else {
      drawer.setAttribute('open', '');
    }
  }

  function resetToSizeTab() {
    var sizeContent = getSizeContent();
    var bundleContent = getBundleContent();
    var postAtc = getPostAtc();
    var footer = getFooter();
    if (sizeContent) sizeContent.classList.remove('purchase-overlay__content--hidden');
    if (bundleContent) bundleContent.classList.add('purchase-overlay__content--hidden');
    if (postAtc) postAtc.classList.add('purchase-overlay__content--hidden');
    if (footer) footer.style.display = '';
    getTabs().forEach(function (t) {
      t.classList.toggle('is-active', t.dataset.overlayTab === 'size');
    });
  }

  function closeOverlay() {
    var drawer = getDrawer();
    state = STATES.CLOSED;
    if (drawer) {
      if (drawer.hide) {
        drawer.hide();
      } else {
        drawer.removeAttribute('open');
      }
    }
    unlockRerender();
    restoreElements();
    resetToSizeTab();
  }

  /* ==================== TABS ==================== */

  function showSizeTab() {
    var sizeContent = getSizeContent();
    var bundleContent = getBundleContent();
    var postAtc = getPostAtc();
    var footer = getFooter();
    var bundleBanner = getBundleBanner();
    var bannerPlus = bundleBanner ? bundleBanner.querySelector('[data-bundle-banner-plus]') : null;
    if (sizeContent) sizeContent.classList.remove('purchase-overlay__content--hidden');
    if (bundleContent) bundleContent.classList.add('purchase-overlay__content--hidden');
    if (postAtc) postAtc.classList.add('purchase-overlay__content--hidden');
    if (footer) footer.style.display = '';
    if (bannerPlus) bannerPlus.textContent = '+';
    if (bundleBanner) bundleBanner.classList.remove('is-active');
    getTabs().forEach(function (t) {
      t.classList.toggle('is-active', t.dataset.overlayTab === 'size');
    });
    updateAtcState();
    setTimeout(updateStockStatus, 50);
  }

  function showBundleTab() {
    var sizeContent = getSizeContent();
    var bundleContent = getBundleContent();
    var postAtc = getPostAtc();
    var footer = getFooter();
    var bundleBanner = getBundleBanner();
    var bundleSlot = getBundleSlot();
    var bannerPlus = bundleBanner ? bundleBanner.querySelector('[data-bundle-banner-plus]') : null;
    if (sizeContent) sizeContent.classList.add('purchase-overlay__content--hidden');
    if (bundleContent) bundleContent.classList.remove('purchase-overlay__content--hidden');
    if (postAtc) postAtc.classList.add('purchase-overlay__content--hidden');
    if (bannerPlus) bannerPlus.textContent = '−';
    if (bundleBanner) bundleBanner.classList.add('is-active');
    getTabs().forEach(function (t) {
      t.classList.toggle('is-active', t.dataset.overlayTab === 'bundle');
    });
    if (footer) footer.style.display = '';
    /* Make bundle visible inside overlay */
    var bEl = bundleSlot ? bundleSlot.querySelector('.product-info__bundle') : null;
    if (bEl) bEl.style.display = '';
    /* Sync ATC button with bundle state */
    syncBundleAtc();
  }

  function showPostAtc() {
    var sizeContent = getSizeContent();
    var bundleContent = getBundleContent();
    var postAtc = getPostAtc();
    var footer = getFooter();
    if (sizeContent) sizeContent.classList.add('purchase-overlay__content--hidden');
    if (bundleContent) bundleContent.classList.add('purchase-overlay__content--hidden');
    if (postAtc) postAtc.classList.remove('purchase-overlay__content--hidden');
    if (footer) footer.style.display = 'none';
  }

  /* ==================== DOM RELOCATION ==================== */

  function relocateElements() {
    /* Upsell — move into the overlay upsell slot */
    var upsellSlot = getUpsellSlot();
    var uEl = doc.querySelector('.product-info__upsell');
    if (uEl && upsellSlot) {
      originalUpsellParent = uEl.parentNode;
      originalUpsellNext = uEl.nextSibling;
      upsellSlot.appendChild(uEl);
      uEl.style.display = '';
    }

    /* Bundle — hide on page, clone not needed since bundle tab has its own content */
    var bundleSlot = getBundleSlot();
    var bEl = doc.querySelector('.product-info__bundle');
    if (bEl && bundleSlot) {
      originalBundleParent = bEl.parentNode;
      originalBundleNext = bEl.nextSibling;
      bEl.style.display = '';
      bundleSlot.appendChild(bEl);
    }
  }

  function restoreElements() {
    /* Restore upsell back to product page (hidden) */
    var upsellSlot = getUpsellSlot();
    var uEl = upsellSlot ? upsellSlot.querySelector('.product-info__upsell') : null;
    if (uEl && originalUpsellParent) {
      uEl.style.display = 'none';
      originalUpsellParent.insertBefore(uEl, originalUpsellNext);
    }

    /* Restore bundle */
    var bundleSlot = getBundleSlot();
    var bEl = bundleSlot ? bundleSlot.querySelector('.product-info__bundle') : null;
    if (bEl && originalBundleParent) {
      bEl.style.display = 'none';
      originalBundleParent.insertBefore(bEl, originalBundleNext);
    }

    originalUpsellParent = originalUpsellNext = null;
    originalBundleParent = originalBundleNext = null;
  }

  /* ==================== HELPERS ==================== */

  function getSelectedPrice() {
    var grid = getGrid();
    if (!grid) return null;
    var checked = grid.querySelector('input:checked');
    if (!checked) return null;
    var swatch = checked.nextElementSibling;
    if (!swatch) return null;

    /* A size covered by the event deal renders two prices — its normal one and
       the deal one — and CSS shows whichever applies to this shopper. Taking the
       first in the DOM order therefore put the pre-discount price on the
       add-to-cart button while the grid beside it showed the deal price. Read the
       one actually on screen instead; offsetParent is null for display:none. */
    var prices = swatch.querySelectorAll('.variant-price');
    for (var i = 0; i < prices.length; i++) {
      if (prices[i].offsetParent !== null) return prices[i].textContent.trim();
    }
    return prices.length ? prices[0].textContent.trim() : null;
  }

  /* ==================== ATC STATE ==================== */

  function updateAtcState() {
    var atcBtn = getAtcBtn();
    if (!atcBtn) return;
    var atcLabel = getAtcLabel();
    var atcPrice = getAtcPrice();
    var grid = getGrid();
    var i18n = getI18n();
    var checked = grid ? grid.querySelector('input:checked') : null;
    if (checked) {
      atcBtn.disabled = false;
      if (atcLabel) atcLabel.textContent = i18n.atc;
      if (atcPrice) atcPrice.textContent = getSelectedPrice() || '';
    } else {
      atcBtn.disabled = true;
      if (atcLabel) atcLabel.textContent = i18n.select;
      if (atcPrice) atcPrice.textContent = '';
    }
  }

  /* ==================== SUBSCRIPTION (selling plan) ==================== */

  function getSubSettings() {
    var cfg = getConfig();
    return (cfg && cfg.subscription) || { planId: null, prices: {} };
  }

  function vpCurrentVariantId() {
    if (confirmedVariantId) return String(confirmedVariantId);
    var formId = getFormId();
    var form = formId ? doc.getElementById(formId) : null;
    var vi = form && form.querySelector('input[name="id"]');
    return (vi && vi.value) ? String(vi.value) : null;
  }
  function vpSelectedPlanId() {
    var drawer = getDrawer();
    var r = drawer ? drawer.querySelector('input[name="selling_plan"]:checked') : null;
    return (r && r.value) ? r.value : null;
  }

  function applySubPrice() {
    var sub = getSubSettings();
    if (!sub.planId) return;
    var drawer = getDrawer();
    if (!drawer) return;
    var vid = vpCurrentVariantId();
    var subPrice = (vid && sub.prices[vid]) ? sub.prices[vid] : null;
    var otPrice = getSelectedPrice();

    var fs = drawer.querySelector('[data-sub-select]');
    if (fs) {
      var cmpMap = {};
      try { cmpMap = JSON.parse(fs.getAttribute('data-sub-compare')); } catch (e) {}
      var cmpPrice = (vid && cmpMap[vid]) ? cmpMap[vid] : otPrice;
      var otEl = fs.querySelector('[data-sub-ot-price]');
      var subEl = fs.querySelector('[data-sub-sub-price]');
      var cmpEl = fs.querySelector('[data-sub-sub-compare]');
      var otCmpEl = fs.querySelector('[data-sub-ot-compare]');
      if (otEl && otPrice) otEl.textContent = otPrice;
      if (subEl && subPrice) subEl.textContent = subPrice;
      if (cmpEl && cmpPrice) cmpEl.textContent = cmpPrice;
      if (otCmpEl) {
        if (cmpPrice && cmpPrice !== otPrice) { otCmpEl.textContent = cmpPrice; otCmpEl.style.display = ''; }
        else { otCmpEl.style.display = 'none'; }
      }

      var subRadio = fs.querySelector('[data-sub-plan="sub"]');
      var otRadio = fs.querySelector('[data-sub-plan="onetime"]');
      var subCard = fs.querySelector('[data-sub-card="sub"]');
      /* Only grey out when we positively know the variant is excluded */
      var avail = !vid || !!subPrice;
      if (subRadio) subRadio.disabled = !avail;
      if (subCard) subCard.classList.toggle('is-unavailable', !avail);
      if (!avail && subRadio && subRadio.checked && otRadio) otRadio.checked = true;
      fs.querySelectorAll('.sub-select__card').forEach(function (c) {
        var inp = c.querySelector('input[name="selling_plan"]');
        c.classList.toggle('is-checked', !!(inp && inp.checked));
      });
    }

    var atcBtn = getAtcBtn();
    var atcPrice = getAtcPrice();
    if (atcBtn && !atcBtn.disabled && atcPrice) {
      var isSub = !!vpSelectedPlanId();
      atcPrice.textContent = (isSub && subPrice) ? subPrice : (otPrice || atcPrice.textContent);
    }
  }

  /* ==================== BUNDLE ATC SYNC ==================== */

  function syncBundleAtc() {
    if (state !== STATES.BUNDLE) return;
    var atcBtn = getAtcBtn();
    if (!atcBtn) return;
    var atcLabel = getAtcLabel();
    var atcPrice = getAtcPrice();
    var bundleBanner = getBundleBanner();
    var i18n = getI18n();
    var bannerPriceEl = bundleBanner ? bundleBanner.querySelector('[data-bundle-banner-price]') : null;
    var bundlePriceText = bannerPriceEl ? bannerPriceEl.textContent.trim() : '';
    if (bundleReady) {
      atcBtn.disabled = false;
      if (atcLabel) atcLabel.textContent = i18n.atc;
      if (atcPrice) atcPrice.textContent = bundlePriceText;
    } else {
      atcBtn.disabled = true;
      if (atcLabel) atcLabel.textContent = i18n.select;
      if (atcPrice) atcPrice.textContent = bundlePriceText;
    }
  }

  /* ==================== ADD TO CART ==================== */

  function addToCart() {
    var atcBtn = getAtcBtn();
    var atcLabel = getAtcLabel();
    var atcPrice = getAtcPrice();
    var drawer = getDrawer();
    var i18n = getI18n();

    /* If in bundle mode, delegate to the bundle's hidden ATC button */
    if (state === STATES.BUNDLE) {
      var hiddenBundleBtn = doc.querySelector('[data-bundle-add]');
      if (hiddenBundleBtn && !hiddenBundleBtn.disabled) {
        hiddenBundleBtn.click();
      }
      return;
    }
    if (!atcBtn || atcBtn.disabled) return;

    /* Subscription confirmation gate — explain terms before committing */
    if (vpSelectedPlanId() && !subConfirmed) {
      var cpanel = drawer ? drawer.querySelector('[data-sub-confirm]') : null;
      if (cpanel) {
        cpanel.hidden = false;
        cpanel.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        return;
      }
    }

    var formId = getFormId();
    var variantId;
    if (confirmedVariantId) {
      variantId = parseInt(confirmedVariantId, 10);
    } else {
      var form0 = formId ? doc.getElementById(formId) : null;
      if (!form0) return;
      var variantInput = form0.querySelector('input[name="id"]');
      if (!variantInput || !variantInput.value) return;
      variantId = parseInt(variantInput.value, 10);
    }

    atcBtn.disabled = true;
    if (atcLabel) atcLabel.textContent = i18n.adding;
    if (atcPrice) atcPrice.textContent = '';

    /* Collect line item properties from the product form */
    var itemProperties = {};
    var form = formId ? doc.getElementById(formId) : null;
    if (form) {
      var propInputs = form.querySelectorAll('[name^="properties["]');
      propInputs.forEach(function (input) {
        if (input.disabled) return;
        var key = input.name.replace('properties[', '').replace(']', '');
        if (input.type === 'checkbox') {
          if (input.checked) itemProperties[key] = input.value || 'true';
        } else if (input.value) {
          itemProperties[key] = input.value;
        }
      });
    }

    /* Add preorder property based on stock status */
    var isInStock = false;
    if (confirmedVariantId) {
      /* For confirmed color variants, check data attributes on the color thumb */
      var activeThumb = doc.querySelector('[data-color-thumb].is-active');
      if (activeThumb) {
        isInStock = activeThumb.dataset.colorAvailable === 'true' && parseInt(activeThumb.dataset.colorQty || '0', 10) > 0;
      }
    } else {
      var stockInfo = getStockMap()[String(variantId)];
      isInStock = stockInfo && stockInfo.available && stockInfo.qty > 0;
    }
    if (!isInStock) {
      itemProperties[i18n.preorderProperty] = i18n.preorderValue;
    }

    var mainItem = {
      id: variantId,
      quantity: 1
    };
    var vpPlan = vpSelectedPlanId();
    if (vpPlan) {
      mainItem.selling_plan = parseInt(vpPlan, 10);
    }
    if (Object.keys(itemProperties).length > 0) {
      mainItem.properties = itemProperties;
    }

    var body = {
      items: [mainItem]
    };

    /* Check upsell toggle (inside drawer footer) */
    var upsellCheckbox = doc.querySelector('.product-upsell__checkbox:checked');
    if (upsellCheckbox) {
      var upsellVariantId = upsellCheckbox.dataset.upsellVariantId;
      if (upsellVariantId) {
        body.items.push({
          id: parseInt(upsellVariantId, 10),
          quantity: 1,
          properties: {
            _upsell: 'true',
            _product_upsell: 'true',
            _upsell_source: 'purchase-overlay-upsell',
            _upsell_reason: 'erp-upsell',
            _upsell_for: (upsellCheckbox.closest('.product-upsell') || upsellCheckbox).getAttribute('data-upsell-for') || ''
          }
        });
      }
    }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function (res) {
        if (!res.ok) throw new Error('Cart add failed');
        return res.json();
      })
      .then(function () {
        /* Refresh cart + close purchase drawer + open cart drawer */
        doc.dispatchEvent(new CustomEvent('cart:refresh'));
        closeOverlay();

        /* Open cart drawer after purchase overlay finishes closing */
        setTimeout(function () {
          var cartDrawer = doc.getElementById('cart-drawer');
          if (cartDrawer && cartDrawer.show) {
            cartDrawer.show();
          }
        }, 350);
      })
      .catch(function (err) {
        console.error('ATC error:', err);
        var btn = getAtcBtn();
        var lbl = getAtcLabel();
        var prc = getAtcPrice();
        if (btn) btn.disabled = false;
        if (lbl) lbl.textContent = getI18n().atc;
        if (prc) prc.textContent = '';
      });
  }

  function openCartDrawer() {
    var cartDrawer = doc.getElementById('cart-drawer');
    if (cartDrawer && cartDrawer.show) {
      cartDrawer.show();
    } else {
      window.location.href = '/cart';
    }
  }

  /* ==================== HEADER PRICE ==================== */

  /* Swap the pre-rendered per-variant price block (rerender is locked while open) */
  function updateHeaderPrice() {
    var drawer = getDrawer();
    var headerPriceWrap = drawer ? drawer.querySelector('[data-overlay-header-price-wrap]') : null;
    if (!headerPriceWrap) return;
    var formId = getFormId();
    var form = formId ? doc.getElementById(formId) : null;
    if (!form) return;
    var variantInput = form.querySelector('input[name="id"]');
    if (!variantInput || !variantInput.value) return;
    var vid = String(variantInput.value);
    headerPriceWrap.querySelectorAll('[data-overlay-header-price-variant]').forEach(function (el) {
      el.style.display = el.getAttribute('data-overlay-header-price-variant') === vid ? 'contents' : 'none';
    });
  }

  /* ==================== COLOR SWITCHER ==================== */

  function onColorThumbClick(thumb) {
    var drawer = getDrawer();
    if (!drawer) return;
    var colorRow = drawer.querySelector('[data-color-row]');
    var colorThumbs = colorRow ? colorRow.querySelectorAll('[data-color-thumb]') : [];
    var headerDefault = $('[data-overlay-header-default]');
    var headerColor = $('[data-overlay-header-color]');
    var colorHeaderImg = $('[data-color-header-img]');
    var colorHeaderTitle = $('[data-color-header-title]');
    var colorHeaderPrice = $('[data-color-header-price]');
    var confirmedColorEl = drawer.querySelector('[data-confirmed-color]');
    var confirmedColorImg = drawer.querySelector('[data-confirmed-color-img]');
    var confirmedColorName = drawer.querySelector('[data-confirmed-color-name]');
    var confirmedColorPrice = drawer.querySelector('[data-confirmed-color-price]');
    var sizeContent = getSizeContent();
    var stockStatusEl = getStockStatusEl();
    var bundleBanner = getBundleBanner();
    var atcBtn = getAtcBtn();
    var atcLabel = getAtcLabel();
    var atcPrice = getAtcPrice();
    var i18n = getI18n();

    colorThumbs.forEach(function (t) { t.classList.remove('is-active'); });
    thumb.classList.add('is-active');

    if (thumb.dataset.colorMode === 'blind-box') {
      /* Switch back to blind box */
      confirmedVariantId = null;
      if (headerDefault) headerDefault.style.display = '';
      if (headerColor) headerColor.style.display = 'none';
      if (sizeContent) sizeContent.style.display = '';
      if (confirmedColorEl) confirmedColorEl.style.display = 'none';
      if (stockStatusEl) stockStatusEl.style.display = 'flex';
      if (bundleBanner && hasBundle) bundleBanner.style.display = '';
      updateAtcState();
      updateHeaderPrice();
    } else {
      /* Switch to confirmed color */
      confirmedVariantId = thumb.dataset.colorVariantId;

      /* Swap header */
      if (headerDefault) headerDefault.style.display = 'none';
      if (headerColor) headerColor.style.display = '';
      if (colorHeaderImg) { colorHeaderImg.src = thumb.dataset.colorImg; colorHeaderImg.alt = thumb.dataset.colorTitle; }
      if (colorHeaderTitle) colorHeaderTitle.textContent = thumb.dataset.colorTitle;
      if (colorHeaderPrice) {
        var cp = thumb.dataset.colorComparePrice;
        colorHeaderPrice.innerHTML = cp
          ? '<s style="color:rgb(var(--text-color)/.4);text-decoration:line-through">' + cp + '</s> ' + thumb.dataset.colorPrice
          : thumb.dataset.colorPrice;
      }

      /* Swap body: hide grid, show color card */
      if (sizeContent) sizeContent.style.display = 'none';
      if (stockStatusEl) stockStatusEl.style.display = 'none';
      if (bundleBanner) bundleBanner.style.display = 'none';
      if (confirmedColorEl) confirmedColorEl.style.display = '';
      if (confirmedColorImg) { confirmedColorImg.src = thumb.dataset.colorImg; confirmedColorImg.alt = thumb.dataset.colorTitle; }
      if (confirmedColorName) confirmedColorName.textContent = thumb.dataset.colorTitle;
      if (confirmedColorPrice) {
        var cp2 = thumb.dataset.colorComparePrice;
        confirmedColorPrice.innerHTML = cp2
          ? '<s>' + cp2 + '</s> ' + thumb.dataset.colorPrice
          : thumb.dataset.colorPrice;
      }

      if (atcBtn) atcBtn.disabled = false;
      if (atcLabel) atcLabel.textContent = i18n.atc;
      if (atcPrice) atcPrice.textContent = thumb.dataset.colorPrice;
    }
  }

  /* ==================== SIZE TIP ==================== */

  function applySizeTip() {
    var cfg = getConfig();
    if (!cfg || !cfg.sizeTip) return;
    var rules = cfg.sizeTip.rules;
    if (!rules) return;
    var tips = cfg.sizeTip.tips || {};
    var title = cfg.sizeTip.title || '';
    var entries = Object.entries(rules);
    for (var i = 0; i < entries.length; i++) {
      var keywords = entries[i][0].split(',');
      var tipKey = entries[i][1];
      var allMatch = keywords.every(function (kw) {
        var trimmed = kw.trim().toLowerCase();
        if (trimmed.charAt(0) === '!') {
          return title.indexOf(trimmed.slice(1)) === -1;
        }
        return title.indexOf(trimmed) !== -1;
      });
      if (allMatch && tips[tipKey]) {
        var el = doc.querySelector('[data-size-tip]');
        if (el) {
          el.textContent = tips[tipKey];
          el.hidden = false;
        }
        break;
      }
    }
  }

  /* ==================== DELEGATED EVENT LISTENERS ==================== */

  doc.addEventListener('click', function (e) {
    var target = e.target;
    if (!(target instanceof Element)) return;

    /* Size chart: manual open to avoid nested aria-controls interference */
    var sizeChartBtn = target.closest('[data-open-size-chart]');
    if (sizeChartBtn) {
      e.preventDefault();
      e.stopPropagation();
      var targetId = sizeChartBtn.getAttribute('data-open-size-chart');
      var sizeChartDrawer = doc.getElementById(targetId);
      if (sizeChartDrawer) {
        if (sizeChartDrawer.show) {
          sizeChartDrawer.show();
        } else {
          sizeChartDrawer.setAttribute('open', '');
        }
      }
      return;
    }

    /* Trigger open */
    if (target.closest('[data-purchase-overlay-trigger]')) {
      if (state === STATES.CLOSED) openOverlay();
      return;
    }

    /* Tab switching */
    var tab = target.closest('[data-overlay-tab]');
    if (tab) {
      var t = tab.dataset.overlayTab;
      if (t === 'size') setState(STATES.SIZE_SELECT);
      if (t === 'bundle') setState(STATES.BUNDLE);
      return;
    }

    /* Bundle banner click — toggle between size and bundle */
    if (target.closest('[data-bundle-banner]')) {
      setState(state === STATES.BUNDLE ? STATES.SIZE_SELECT : STATES.BUNDLE);
      return;
    }

    /* ATC button */
    if (target.closest('[data-purchase-overlay-atc]')) {
      addToCart();
      return;
    }

    /* "Go to cart" button in post-ATC */
    if (target.closest('[data-overlay-open-cart]')) {
      closeOverlay();
      openCartDrawer();
      return;
    }

    /* "Continue shopping" close button */
    if (target.closest('[data-purchase-overlay-close]')) {
      closeOverlay();
      return;
    }

    /* Color switcher thumbs */
    var thumb = target.closest('[data-color-thumb]');
    if (thumb) {
      onColorThumbClick(thumb);
      return;
    }

    /* Subscription confirmation panel buttons */
    if (target.closest('[data-sub-confirm-ok]')) {
      var drawerOk = getDrawer();
      var panelOk = drawerOk ? drawerOk.querySelector('[data-sub-confirm]') : null;
      subConfirmed = true;
      if (panelOk) panelOk.hidden = true;
      addToCart();
      return;
    }
    if (target.closest('[data-sub-confirm-cancel]')) {
      var drawerC = getDrawer();
      var panelC = drawerC ? drawerC.querySelector('[data-sub-confirm]') : null;
      if (panelC) panelC.hidden = true;
      return;
    }
  });

  doc.addEventListener('change', function (e) {
    var target = e.target;
    if (!(target instanceof Element)) return;

    /* Size selection — update ATC + price + stock status, do NOT close overlay */
    var grid = getGrid();
    if (grid && grid.contains(target) && target.matches('input[type="radio"]')) {
      updateAtcState();
      var price = getSelectedPrice();
      var priceEl = $('[data-overlay-price]');
      if (price && priceEl) priceEl.textContent = price;
      /* Defer to next tick so theme updates input[name="id"] first */
      setTimeout(function () { updateStockStatus(); updateHeaderPrice(); applySubPrice(); }, 50);
      return;
    }

    /* Subscription plan toggle — re-confirm whenever the plan changes */
    if (target.matches('input[name="selling_plan"]') && target.closest('[data-purchase-overlay]')) {
      subConfirmed = false;
      var drawer = getDrawer();
      var cp = drawer ? drawer.querySelector('[data-sub-confirm]') : null;
      if (cp) cp.hidden = true;
      applySubPrice();
    }
  });

  /* Escape closes the subscription confirmation panel */
  doc.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    var drawer = getDrawer();
    var panel = drawer ? drawer.querySelector('[data-sub-confirm]') : null;
    if (panel && !panel.hidden) panel.hidden = true;
  });

  /* Native drawer close event — clean up state (bubbles) */
  doc.addEventListener('dialog:after-hide', function (e) {
    var target = e.target;
    if (!(target instanceof Element) || !target.hasAttribute('data-purchase-overlay')) return;
    if (state !== STATES.CLOSED) {
      state = STATES.CLOSED;
      unlockRerender();
      restoreElements();
      resetToSizeTab();
    }
  });

  /* Bundle interface state (dispatched on document by the bundle block) */
  doc.addEventListener('bundle:state', function (e) {
    bundleReady = !!(e.detail && e.detail.ready);
    syncBundleAtc();

    /* Only update stock status when in bundle mode */
    if (state !== STATES.BUNDLE) return;
    var stockStatusEl = getStockStatusEl();
    var stockTextEl = getStockTextEl();
    if (!stockStatusEl || !stockTextEl) return;

    /* Debounce to prevent flickering */
    if (bundleStockTimer) clearTimeout(bundleStockTimer);
    bundleStockTimer = setTimeout(function () {
      var sEl = getStockStatusEl();
      var tEl = getStockTextEl();
      var iEl = getStockIconEl();
      if (!sEl || !tEl) return;
      var i18n = getI18n();
      var items = e.detail && e.detail.stockItems;
      if (!items || items.length === 0) { sEl.style.display = 'none'; return; }

      var lines = [];
      for (var si = 0; si < items.length; si++) {
        var it = items[si];
        var status, icon;
        var bundleLabel = it.product ? it.product + ' ' + it.title : it.title;
        if (!it.available) {
          status = bundleLabel + ' — ' + i18n.stockSoldOut;
          icon = '';
        } else if (it.qty > 0 && it.qty <= 5) {
          status = bundleLabel + ' — ' + i18n.stockLowBare.replace('__COUNT__', it.qty);
          icon = '<svg width="14" height="14" viewBox="0 0 512 512" style="flex-shrink:0"><use href="#48h-icon"></use></svg>';
        } else if (it.qty > 5) {
          status = bundleLabel + ' — ' + i18n.stockFewBare;
          icon = '<svg width="14" height="14" viewBox="0 0 512 512" style="flex-shrink:0"><use href="#48h-icon"></use></svg>';
        } else if (it.backorderDays > 0) {
          var itWeeks = Math.max(1, Math.round(it.backorderDays / 7));
          status = bundleLabel + ' — ' + (itWeeks === 1 ? i18n.stockBackorder1 : String(i18n.stockBackorderN || '').replace('__COUNT__', itWeeks));
          icon = '<svg width="14" height="14" viewBox="0 0 24 24" style="flex-shrink:0;color:var(--sneak-green)"><use href="#backorder-icon"></use></svg>';
        } else if (it.incoming || it.policy === 'continue') {
          status = bundleLabel + ' — ' + i18n.stockIncoming;
          icon = '<svg width="14" height="14" viewBox="0 0 64 64" style="flex-shrink:0;color:var(--sneak-green)"><use href="#incoming-icon"></use></svg>';
        } else {
          status = bundleLabel + ' — ' + i18n.stockPreorder;
          icon = '';
        }
        lines.push('<span style="display:flex;align-items:center;gap:5px">' + icon + status + '</span>');
      }
      if (lines.length === 0) { sEl.style.display = 'none'; return; }

      sEl.style.display = 'block';
      if (iEl) iEl.style.display = 'none';
      tEl.innerHTML = lines.join('');
    }, 100);
  });

  /* Re-evaluate sub price once the theme resolves the variant (bubbles) */
  doc.addEventListener('variant:change', function (e) {
    var formId = getFormId();
    if (!formId) return;
    var detailFormId = e.detail && e.detail.formId;
    var targetForm = (e.target instanceof Element) ? e.target.closest('form') || e.target : null;
    if (detailFormId === formId || (targetForm && targetForm.id === formId)) {
      setTimeout(applySubPrice, 0);
    }
  });

  /* ==================== INIT (idempotent, re-run after rerender) ==================== */

  function initInstance() {
    if (!getTrigger() || !getDrawer()) return;
    applySizeTip();
    detectBundle();
    updateAtcState();
    applySubPrice();
    /* Defer stock status — theme may not have set input[name="id"] yet */
    setTimeout(function () { updateStockStatus(); updateHeaderPrice(); }, 100);
  }

  /* The Section Rendering API re-render replaces the variant-picker block with
   * DOMParser-created nodes (inline scripts never execute), so re-run the
   * idempotent init after each product:rerender. The event does not bubble —
   * capture phase still observes it on the document. */
  doc.addEventListener('product:rerender', function () {
    setTimeout(initInstance, 50);
  }, true);

  /* ==================== SIZE-GRID SCROLL PRESERVATION ==================== */
  /*
   * Picking a size threw the grid back to the top, mid-choice.
   *
   * The size radios are hidden with .sr-only, which is position:absolute, and
   * they are SIBLINGS of the swatch labels rather than children. Every ancestor
   * between them and .purchase-overlay__content is position:static, so all of
   * them resolve against that scroller and stack in its top-left corner. Clicking
   * a swatch focuses its radio natively, and the browser scrolls the focused
   * element into view — that corner — which means scrolling the grid to the top.
   * Nothing in this file was calling scrollTop; the browser was doing it.
   *
   * Anchoring each input to its own swatch would take a markup change, since a
   * sibling cannot be positioned against an element it is not inside. So the
   * offset is captured before focus moves and restored on the next frame instead.
   * Focus still lands on the right input, so keyboard and screen-reader behaviour
   * is unchanged — only the involuntary scroll is undone.
   */
  var scrollKeeper = null;

  doc.addEventListener('pointerdown', function (event) {
    var target = event.target;
    var swatchArea = target && target.closest ? target.closest('.variant-picker__option-values') : null;
    if (!swatchArea) {
      scrollKeeper = null;
      return;
    }
    var scroller = swatchArea.closest('.purchase-overlay__content');
    scrollKeeper = scroller ? { el: scroller, top: scroller.scrollTop } : null;
  }, true);

  doc.addEventListener('focusin', function (event) {
    if (!scrollKeeper) return;
    var input = event.target;
    if (!input || !input.matches || !input.matches('input.sr-only')) return;

    var keeper = scrollKeeper;
    scrollKeeper = null;
    if (keeper.el.scrollTop === keeper.top) return;

    keeper.el.scrollTop = keeper.top;
    /* Some browsers run their own scroll adjustment after the focus event, so
       re-assert once on the next frame rather than trusting the first write. */
    requestAnimationFrame(function () {
      if (keeper.el.scrollTop !== keeper.top) keeper.el.scrollTop = keeper.top;
    });
  });

  function boot() {
    initInstance();
    tickAllCountdowns();
    setInterval(tickAllCountdowns, 1000);
  }

  if (doc.readyState === 'loading') {
    doc.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
